import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Autocomplete, Box, Chip, CircularProgress, Divider, Grid, IconButton, MenuItem, Paper, Select, TextField, Tooltip, Typography } from '@mui/material';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import { useParams } from 'react-router-dom'
import NoDataAvailableCard from './NoDataAvailableCard';
import GraphCard from './GraphCard';
import StatTable from './StatTable';
import CardSkeleton from './CardSkeleton';
import { globalGetService } from '../../../utils/globalApiServices';
import { checkApiStatus } from '../../../utils';
import { getUtilizationAssetsApi } from '../apiService';

const UtilizationMonthlyTrends = ({ utilizationData, isLoading = {}, assetData, onAssetChange, utilType, onMetricChange, utilVal, onPeriodChange }) => {
  const params = useParams();
  const isMonthlyUtilLoading = isLoading?.monthly_utilization
  const [isDrillDown, setDrillDown] = useState(false);
  const [majorAssemblyOptions, setMajorAssemblyOptions] = useState([])
  const [majorAssemblyLoading, setMajorAssemblyLoading] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState(null)
  const monthlyUtilPaperRef = useRef(null)
  const [monthlyUtilPaperHeight, setMonthlyUtilPaperHeight] = useState(null)

  const monthlyUtil = utilizationData?.monthly_utilization;

  useEffect(() => {
    getUtilizationAssets();
  }, [])

  useEffect(() => {
    const el = monthlyUtilPaperRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setMonthlyUtilPaperHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [])

  useEffect(() => {
    setSelectedMonth(null);
    if (!monthlyUtil?.options) setDrillDown(false);
  }, [monthlyUtil])

  const handleMonthClick = (chartParams) => {
    if (chartParams?.componentType !== 'series') return;
    const trendItem = monthlyUtil?.utilsTrendsData?.[chartParams?.dataIndex];
    if (!trendItem?.month) return;
    setSelectedMonth(trendItem.month);
    setDrillDown(true);
  }

  const drillDownRows = useMemo(() => (
    selectedMonth
      ? (monthlyUtil?.table?.rows || []).filter(row => row.period === selectedMonth)
      : monthlyUtil?.table?.rows
  ), [selectedMonth, monthlyUtil])

  const getUtilizationAssets = () => {
    setMajorAssemblyLoading(true)
    globalGetService(getUtilizationAssetsApi(params)).then(response => {
      setMajorAssemblyLoading(false)
      if (checkApiStatus(response)) {
        setMajorAssemblyOptions(response?.data?.data || [])
      }
    })
  }
  return (
    <div className="contract-analytics-wrapper">
      <div className="ca-page-header">
        <Typography fontSize='15px' fontFamily="Roboto-SemiBold !important">Utilization & Component Trends </Typography>
      </div>
      <Grid container spacing={1.5} mx={1}>
        <Grid size={{ md: 8, xs: 12 }}>
          <Paper ref={monthlyUtilPaperRef}>
            <div className="ca-card-header">
              <Typography className="ca-card-title"> Monthly Utilization Trend — Actual FH & FC </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Autocomplete
                  disablePortal
                  disableClearable
                  sx={{
                    width: 180,
                    '& .MuiOutlinedInput-root': { height: 34, fontSize: '12px' },
                    '& .MuiOutlinedInput-input': { padding: '2px 4px !important' },
                  }}
                  options={majorAssemblyOptions}
                  isOptionEqualToValue={(option, value) => option?.id === value?.asset_id}
                  disabled={isMonthlyUtilLoading || majorAssemblyLoading}
                  getOptionLabel={(option) => option?.name || ""}
                  noOptionsText={majorAssemblyLoading ? "loading" : "No Option Available"}
                  value={majorAssemblyOptions.find(item => item?.id === assetData?.asset_id) || null}
                  onChange={(e, value) => onAssetChange(value ? { asset_id: value?.id, asset_type: value?.asset_type, asset_slug: value?.asset_slug, name: value?.name } : null)}
                  renderInput={(params) => {
                    const selectedLabel = params?.inputProps?.value || "";
                    return (
                      <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow>
                        <TextField
                          {...params}
                          margin='none'
                          size='small'
                          fullWidth
                          disabled={isMonthlyUtilLoading || majorAssemblyLoading}
                          slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                              ...params?.InputProps, endAdornment: (<>{majorAssemblyLoading ? (<CircularProgress color="inherit" size={20} />) : null}{params?.InputProps?.endAdornment}</>)
                            }
                          }}
                        />
                      </Tooltip>
                    );
                  }}
                />
                {monthlyUtil?.table?.rows?.length > 0 &&
                  <Tooltip title={isDrillDown ? "Hide drill down" : "Drill down"}>
                    <IconButton
                      size='small'
                      color={isDrillDown ? 'primary' : 'default'}
                      onClick={() => {
                        if (isDrillDown) setSelectedMonth(null);
                        setDrillDown(!isDrillDown);
                      }}
                    >
                      <UnfoldMoreOutlinedIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>}
              </Box>
            </div>
            {isMonthlyUtilLoading ? (
              <CardSkeleton height='260px' className='ca-graph-container' />
            ) : (
              <>
                <div className="ca-graph-container">
                  {!monthlyUtil?.options ? (
                    <NoDataAvailableCard title='No utilization data available' subtext='Try adjusting the date range' />
                  ) : (
                    <GraphCard option={monthlyUtil?.options} height='260px' onChartClick={handleMonthClick} />
                  )}
                </div>
                {isDrillDown &&
                  <div className="ca-drilldown">
                    {selectedMonth &&
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
                        <Typography sx={{ fontSize: 12, color: '#6b7280' }}>Showing:</Typography>
                        <Chip label={selectedMonth} size="small" onDelete={() => setSelectedMonth(null)} />
                      </Box>}
                    {drillDownRows?.length ? (
                      <div className="ca-drilldown-scroll">
                        <StatTable columns={monthlyUtil?.table?.columns} rows={drillDownRows} />
                      </div>
                    ) : selectedMonth ? (
                      <NoDataAvailableCard title={`No component data available for ${selectedMonth}`} subtext='Try selecting another month' />
                    ) : (
                      <NoDataAvailableCard title='No component data available' subtext='Try adjusting the date range' />
                    )}
                  </div>}
              </>
            )}
          </Paper>
        </Grid>
        <Grid size={{ md: 4, xs: 12 }} sx={{ display: 'flex' }}>
          <Paper sx={{ height: monthlyUtilPaperHeight ? `${monthlyUtilPaperHeight}px` : '100%', width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', border: '2px solid #ECEFF3', background: '#F8FBFF',boxSizing: 'border-box' }} >
            <div className="ca-card-header" style={{ alignItems: 'flex-start', padding:'8px 5px'}}>
              <Typography className="ca-card-title"> Average Utilization by Contract Component </Typography>
              <div className="ca-header-actions">
                <Select
                  size="small"
                  value={utilType}
                  disabled={isLoading?.average_utilization}
                  onChange={(e) => onMetricChange(e.target.value)}
                  sx={{ height: 20, width: 75, fontSize: '10px', background: '#FFF' }}
                >
                  <MenuItem value='both'>FH & FC</MenuItem>
                  <MenuItem value='fh'>FH</MenuItem>
                  <MenuItem value='fc'>FC</MenuItem>
                </Select>
                <Select
                  size="small"
                  value={utilVal}
                  disabled={isLoading?.average_utilization}
                  onChange={(e) => onPeriodChange(e.target.value)}
                  sx={{ height: 20, width: 75, fontSize: '11px', background: '#FFF' }}
                >
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </div>
            </div>
            {isLoading?.average_utilization ? (
              <CardSkeleton />
            ) : (
              <div className="ca-card-body" >
                <div className="ca-scroll-table" style={{ marginTop: 5 }}>
                  <Grid container spacing={1} sx={{ px: 1, pb: 1 }}>
                    {!utilizationData?.average_utilization?.items?.length ? (
                    <NoDataAvailableCard title='No average utilization data available' subtext='Try adjusting the date range' />
                  ): utilizationData?.average_utilization?.items?.map((item) => {
                      return (
                        <Grid size={12} key={item.component}>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1, border: "1px solid #E5E7EB", borderRadius: 3, bgcolor: "#FFFFFF", transition: "all .2s ease", boxShadow: "0 1px 3px rgba(15,23,42,.05)", "&:hover": { borderColor: "#CBD5E1", boxShadow: "0 4px 12px rgba(15,23,42,.08)", } }}>
                            <Box sx={{ display: "flex", flexDirection: "column", width: "40%" }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1F2937", lineHeight: 1.2, }} > {item.component} </Typography>
                              <Box sx={{ display: "inline-flex", alignSelf: "flex-start", mt: 0.5, px: 1, py: "1px", bgcolor: "#EEF4FF", color: "#2563EB", borderRadius: 10, fontSize: 10, fontWeight: 600, }} >
                                {item.period_label || "--"}
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              {utilType !== 'fc' &&
                                <Box sx={{ textAlign: "center", minWidth: 70 }}>
                                  <Typography sx={{ fontSize: 10, color: "#64748B" }}> Avg. FH </Typography>
                                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#2563EB", lineHeight: 1.2 }}>
                                    {item.avg_monthly_fh || "--"}
                                    <Typography component="span" sx={{ fontSize: 10, color: "#94A3B8", ml: .3 }}> FH </Typography>
                                  </Typography>
                                </Box>}
                              {utilType === 'both' && <Divider orientation="vertical" flexItem />}
                              {utilType !== 'fh' &&
                                <Box sx={{ textAlign: "center", minWidth: 70 }}>
                                  <Typography sx={{ fontSize: 10, color: "#64748B" }}> Avg. FC </Typography>
                                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#16A34A", lineHeight: 1.2 }}>
                                    {item.avg_monthly_fc || "--"}
                                    <Typography component="span" sx={{ fontSize: 10, color: "#94A3B8", ml: .3 }}> FC </Typography>
                                  </Typography>
                                </Box>}
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </div>
              </div>)}
          </Paper>
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <Paper>
              <div className="ca-card-header">
                <Typography className="ca-card-title"> Component Utilization Trend Lines — FH </Typography>
              </div>
              {isLoading?.fh_utilization ? (
                <CardSkeleton height='250px' className='ca-graph-container' />
              ) : (
                <div className="ca-graph-container">
                  {!utilizationData?.fh_utilization?.options ? (
                    <NoDataAvailableCard title='No utilization data available' subtext='Try adjusting the date range' />
                  ) : (
                    <GraphCard option={utilizationData?.fh_utilization?.options} />
                  )}
                </div>
              )}
          </Paper>
        </Grid>
        <Grid size={{ md: 6, xs: 12 }}>
          <Paper>
            <div className="ca-card-header">
              <Typography className="ca-card-title"> Component Utilization Trend Lines — FC </Typography>
            </div>
            {isLoading?.fc_utilization ? (
              <CardSkeleton height='250px' className='ca-graph-container' />
            ) : (
              <div className="ca-graph-container">
                {!utilizationData?.fc_utilization?.options ? (
                  <NoDataAvailableCard title='No utilization data available' subtext='Try adjusting the date range' />
                ) : (
                  <GraphCard option={utilizationData?.fc_utilization?.options} />
                )}
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default UtilizationMonthlyTrends
